import { UAParser } from 'ua-parser-js';
const signInContextKeys = ['ip', 'userAgent', 'country', 'city'];
const hasOptionalStringProperty = (value, key) => {
    const property = Reflect.get(value, key);
    return property === undefined || typeof property === 'string';
};
const isSignInContext = (value) => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    return signInContextKeys.every((key) => hasOptionalStringProperty(value, key));
};
const getParsedUserAgentInfo = (userAgent) => {
    if (!userAgent) {
        return {};
    }
    const { device, browser, os } = new UAParser(userAgent).getResult();
    const deviceModel = [device.vendor, device.model].filter(Boolean).join(' ') || undefined;
    return {
        browserName: browser.name,
        osName: os.name,
        deviceModel,
    };
};
const formatDeviceName = ({ browserName, osName, deviceModel }) => {
    if (browserName && deviceModel) {
        return `${browserName} on ${deviceModel}`;
    }
    if (browserName && osName) {
        return `${browserName} on ${osName}`;
    }
    if (browserName) {
        return browserName;
    }
    if (deviceModel) {
        return deviceModel;
    }
    return osName;
};
const formatDeviceLocation = ({ country, city }) => {
    const location = [city, country].filter(Boolean).join(', ');
    return location || undefined;
};
/** Derive reusable browser, operating-system, device, and location display information. */
export const getDeviceDisplayInfo = ({ userAgent, country, city, }) => {
    const parsed = getParsedUserAgentInfo(userAgent);
    return {
        name: formatDeviceName(parsed),
        location: formatDeviceLocation({ country, city }),
        city,
        country,
        ...parsed,
    };
};
export const getSessionDisplayInfo = (session) => {
    const signInContext = session.lastSubmission?.signInContext;
    if (!isSignInContext(signInContext)) {
        return {};
    }
    return {
        ...getDeviceDisplayInfo(signInContext),
        ip: signInContext.ip,
    };
};
export const normalizeUserApplicationGrantGroups = (grants) => {
    const groupedByApplicationId = new Map();
    for (const grant of grants) {
        const group = groupedByApplicationId.get(grant.payload.clientId);
        if (!group) {
            groupedByApplicationId.set(grant.payload.clientId, {
                applicationName: grant.application.name,
                iat: grant.payload.iat,
                grantIds: [grant.id],
            });
            continue;
        }
        groupedByApplicationId.set(grant.payload.clientId, {
            applicationName: group.applicationName,
            iat: Math.min(group.iat, grant.payload.iat),
            grantIds: [...group.grantIds, grant.id],
        });
    }
    return Array.from(groupedByApplicationId.entries())
        .map(([applicationId, group]) => ({
        id: applicationId,
        applicationId,
        applicationName: group.applicationName,
        iat: group.iat,
        grantIds: group.grantIds,
    }))
        .toSorted((previous, next) => next.iat - previous.iat);
};
